import { createSelector } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { SiteConfiguration } from '../contexts/SiteConfiguration/SiteConfigContext';
import { RegisteredApplications } from '../types/application.types';
import { Features } from '../types/features';
import { Settings } from '../types/settings';
import { SharedStoreState } from '../types/store';

const selectSelf = (state: SharedStoreState) => state;

export const useSelectIsAppLoading = (): boolean =>
	useSelector<SharedStoreState, boolean>(state => state.loading);

export const useSelectShowWarningBanner = (): boolean =>
	useSelector<SharedStoreState, boolean>(state => state.warningBanner);

export const selectFeatureFlags = createSelector(
	selectSelf,
	state => state.features.variations ?? {},
);

export const selectFeatureFlagsReady = createSelector(
	selectSelf,
	state => state.features.status === 'ready',
);

export const useSelectFeatures = (): Features =>
	useSelector<SharedStoreState, Features>(selectFeatureFlags);

export const useSelectFeaturesLoaded = (): boolean =>
	useSelector<SharedStoreState, boolean>(selectFeatureFlagsReady);

export const useSelectSettings = (): Settings =>
	useSelector<SharedStoreState, Settings>(state => state.settings);

export const useSelectSiteConfig = (): SiteConfiguration =>
	useSelector<SharedStoreState, SiteConfiguration>(state => state.siteConfig);

export const useSelectNamespace = (): RegisteredApplications =>
	useSelector<SharedStoreState, RegisteredApplications>(
		state =>
			state?.applicationContext?.applicationName ??
			RegisteredApplications.UNREGISTERED,
	);

// legacy exports (incorrect hook shape)
export const selectIsAppLoading = useSelectIsAppLoading;
export const selectShowWarningBanner = useSelectShowWarningBanner;
export const selectFeatures = useSelectFeatures;
export const selectSettings = useSelectSettings;
export const selectSiteConfig = useSelectSiteConfig;
export const selectNamespace = useSelectNamespace;
